import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { io } from "socket.io-client";
import { jwtDecode } from "jwt-decode";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom"; // ✅ ADD THIS

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [chatUnreadCounts, setChatUnreadCounts] = useState({});
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [activeMessages, setActiveMessages] = useState(new Map());
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const SOCKET_URL =
    process.env.REACT_APP_SOCKET_URL || "https://tcs.teamexbiz.in";

  const socketRef = useRef(null);
  const currentChatIdRef = useRef(null);
  const queryClient = useQueryClient();
  const invalidateChatsTimeoutRef = useRef(null);
  const navigate = useNavigate(); // ✅ ADD THIS
  // ✅ Use ref to avoid stale closure issues
  const notificationsRef = useRef(notifications);

  // Keep ref in sync with state
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("token");
      setIsAuthenticated(!!token);
    };

    checkAuth();
    window.addEventListener("storage", checkAuth);
    const handleAuthChange = () => checkAuth();
    window.addEventListener("authChange", handleAuthChange);

    return () => {
      window.removeEventListener("storage", checkAuth);
      window.removeEventListener("authChange", handleAuthChange);
    };
  }, []);

  // Request notification permission
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          console.log("✅ Notifications enabled");
        }
      });
    }
  }, []);

  const getCurrentUserId = useCallback(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const decoded = jwtDecode(token);
      return decoded.id || decoded._id || decoded.userid || null;
    } catch {
      return null;
    }
  }, []);

  // ✅ Debounced invalidate function
  const debouncedInvalidateChats = useCallback(() => {
    if (invalidateChatsTimeoutRef.current) {
      clearTimeout(invalidateChatsTimeoutRef.current);
    }
    
    invalidateChatsTimeoutRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ 
        queryKey: ["chats"],
        refetchType: 'active'
      });
    }, 1000);
  }, [queryClient]);

  const showBrowserNotification = useCallback((notification) => {
    if (!("Notification" in window) || Notification.permission !== "granted") {
      return;
    }

    try {
      const title = notification.sender?.username || "New Message";
      const body =
        notification.fileType === "text"
          ? notification.content
          : `Sent a ${notification.fileType}`;

      const browserNotification = new Notification(title, {
        body: body,
        icon: notification.sender?.avatar || "/chat-icon.png",
        badge: "/chat-icon.png",
        tag: notification.chatId,
        requireInteraction: false,
        silent: false,
      });

      browserNotification.onclick = () => {
        window.focus();
        window.location.href = `/chats?chatId=${notification.chatId}`;
        browserNotification.close();
      };

      setTimeout(() => {
        browserNotification.close();
      }, 5000);
    } catch (error) {
      console.error("Error showing notification:", error);
    }
  }, []);

  // Socket connection logic - FIXED: Removed notifications from dependency array
  useEffect(() => {
    if (!isAuthenticated) {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setOnlineUsers([]);
        setNotifications([]);
        setUnreadNotificationCount(0);
        setTotalUnreadCount(0);
        setChatUnreadCounts({});
        setConnectionStatus("disconnected");
        setActiveMessages(new Map());
      }
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) return;

    const newSocket = io(SOCKET_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    newSocket.on("connect", () => {
      setConnectionStatus("connected");
      newSocket.emit("getUnreadCounts");
    });

    newSocket.on("disconnect", () => {
      setConnectionStatus("disconnected");
    });

    newSocket.on("connect_error", (error) => {
      setConnectionStatus("error");
    });

    newSocket.on("reconnect", () => {
      setConnectionStatus("connected");
      newSocket.emit("getUnreadCounts");
    });

    newSocket.on("userOnline", ({ userId, username }) => {
      setOnlineUsers((prev) =>
        prev.includes(userId) ? prev : [...prev, userId]
      );
    });

    newSocket.on("userOffline", ({ userId, username }) => {
      setOnlineUsers((prev) => prev.filter((id) => id !== userId));
    });

    newSocket.on("userStatusUpdate", ({ userId, isOnline, username }) => {
      setOnlineUsers((prev) =>
        isOnline
          ? [...new Set([...prev, userId])]
          : prev.filter((id) => id !== userId)
      );
    });

    newSocket.on("newMessage", (messageData) => {
      setActiveMessages((prev) => {
        const newMap = new Map(prev);
        newMap.set(messageData._id, messageData);
        return newMap;
      });

      const currentUserId = getCurrentUserId();
      
      if (messageData.sender?._id !== currentUserId) {
        debouncedInvalidateChats();
      }

      newSocket.emit("getUnreadCounts");
    });

    newSocket.on("messageNotification", (notification) => {
      const processed = {
        id: notification.messageId || notification.id,
        chatId: notification.chatId,
        messageId: notification.messageId,
        sender: notification.sender,
        content: notification.content,
        fileType: notification.fileType || "text",
        timestamp: notification.timestamp || new Date().toISOString(),
        chatName: notification.chatName,
        read: false,
      };

      const isCurrentChat = currentChatIdRef.current === notification.chatId;

      setNotifications((prev) => {
        const exists = prev.find((n) => n.id === processed.id);
        if (exists) return prev;
        return [processed, ...prev];
      });

      if (!isCurrentChat) {
        setUnreadNotificationCount((prev) => prev + 1);
        showBrowserNotification(processed);
      }

      if (window.showToast && !isCurrentChat) {
        window.showToast({
          chatName: processed.chatName,
          title: processed.sender.username,
          message: processed.content,
          type: "info",
          duration: 4000,
        });
      }
    });

    newSocket.on("unreadCounts", ({ chatCounts, totalCount }) => {
      setChatUnreadCounts(chatCounts || {});
      setTotalUnreadCount(totalCount || 0);
    });

    newSocket.on(
      "chatUnreadCountsUpdated",
      ({ chatId, userId, unreadCount }) => {
        const currentUserId = getCurrentUserId();
        if (userId !== currentUserId) return;

        setChatUnreadCounts((prev) => {
          const updated = { ...prev, [chatId]: unreadCount };
          const newTotal = Object.values(updated).reduce((a, b) => a + b, 0);
          setTotalUnreadCount(newTotal);
          return updated;
        });

        debouncedInvalidateChats();
      }
    );

    newSocket.on("notificationsCleared", ({ chatId }) => {
      setNotifications((prev) => prev.filter((n) => n.chatId !== chatId));
      setUnreadNotificationCount((prev) => {
        // ✅ Use ref to get current notifications
        const removedCount = notificationsRef.current.filter(
          (n) => n.chatId === chatId && !n.read
        ).length;
        return Math.max(0, prev - removedCount);
      });

      debouncedInvalidateChats();
    });

    newSocket.on("chatNotificationsCleared", ({ chatId }) => {
      setChatUnreadCounts((prev) => {
        const updated = { ...prev, [chatId]: 0 };
        const newTotal = Object.values(updated).reduce((a, b) => a + b, 0);
        setTotalUnreadCount(newTotal);
        return updated;
      });

      setNotifications((prev) => prev.filter((n) => n.chatId !== chatId));
      setUnreadNotificationCount((prev) => {
        // ✅ Use ref to get current notifications
        const removedCount = notificationsRef.current.filter(
          (n) => n.chatId === chatId && !n.read
        ).length;
        return Math.max(0, prev - removedCount);
      });

      debouncedInvalidateChats();
    });

    // ✅ NEW: Handle group deletion event
    newSocket.on("groupDeleted", (data) => {
      const { chatId, deletedBy, groupName, message } = data;
      
      console.log("🗑️ Group deleted:", data);

      // Remove chat from ALL query caches
      const chatQueries = queryClient.getQueriesData({ queryKey: ["chats"] });
      
      chatQueries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        queryClient.setQueryData(queryKey, (old) => {
          if (!old) return old;

          const updatedPages = old.pages.map((page) => ({
            ...page,
            chats: page.chats.filter((chat) => chat._id !== chatId),
          }));

          return {
            ...old,
            pages: updatedPages,
          };
        });
      });

      // Remove specific chat-related queries
      queryClient.removeQueries(["groupMembers", chatId]);
      queryClient.removeQueries(["chatMessages", chatId]);
      queryClient.removeQueries(["userChats", chatId]);

      // Clear notifications for this chat
      setNotifications((prev) => prev.filter((n) => n.chatId !== chatId));
      setChatUnreadCounts((prev) => {
        const updated = { ...prev };
        delete updated[chatId];
        const newTotal = Object.values(updated).reduce((a, b) => a + b, 0);
        setTotalUnreadCount(newTotal);
        return updated;
      });

      // Show notification to user
      const notificationMessage = message || `Group "${groupName}" has been deleted`;
      
      if (window.showToast) {
        window.showToast({
          title: "Group Deleted",
          message: notificationMessage,
          type: "error",
          duration: 5000,
        });
      } else {
        alert(notificationMessage);
      }

      // Navigate away if viewing this chat
      const currentPath = window.location.pathname;
      if (currentPath.includes(chatId) || currentChatIdRef.current === chatId) {
        currentChatIdRef.current = null;
        navigate("/chats", { replace: true });
      }
    });

    newSocket.on("userTyping", ({ chatId, userId, username }) => {});

    newSocket.on("userStoppedTyping", ({ chatId, userId, username }) => {});

    newSocket.on("messagesRead", ({ chatId, userId, readAt }) => {
      queryClient.setQueryData(["chatMessages", chatId], (oldData) => {
        if (!oldData) return oldData;

        const currentUserId = getCurrentUserId();
        const updatedPages = oldData.pages.map((page) => ({
          ...page,
          messages: page.messages.map((msg) => {
            if (msg.sender._id === currentUserId) {
              const existingReadBy = Array.isArray(msg.readBy) ? msg.readBy : [];
              const alreadyRead = existingReadBy.some((u) => {
                const readUserId = typeof u === 'string' ? u : u._id;
                return readUserId === userId;
              });
              
              if (!alreadyRead) {
                return {
                  ...msg,
                  readBy: [...existingReadBy, userId],
                };
              }
            }
            return msg;
          }),
        }));

        return {
          ...oldData,
          pages: updatedPages,
        };
      });

      debouncedInvalidateChats();
    });

    newSocket.on("messageRead", ({ chatId, messageId, userId }) => {
      queryClient.setQueryData(["chatMessages", chatId], (oldData) => {
        if (!oldData) return oldData;

        const updatedPages = oldData.pages.map((page) => ({
          ...page,
          messages: page.messages.map((msg) => {
            if (msg._id === messageId) {
              const existingReadBy = Array.isArray(msg.readBy) ? msg.readBy : [];
              const alreadyRead = existingReadBy.some((u) => {
                const readUserId = typeof u === 'string' ? u : u._id;
                return readUserId === userId;
              });
              
              if (!alreadyRead) {
                return {
                  ...msg,
                  readBy: [...existingReadBy, userId],
                };
              }
            }
            return msg;
          }),
        }));

        return {
          ...oldData,
          pages: updatedPages,
        };
      });

      debouncedInvalidateChats();
    });

    newSocket.on("messageSeen", ({ chatId, messageId, seenBy }) => {
      queryClient.setQueryData(["chatMessages", chatId], (oldData) => {
        if (!oldData) return oldData;

        const updatedPages = oldData.pages.map((page) => ({
          ...page,
          messages: page.messages.map((msg) => {
            if (msg._id === messageId) {
              return {
                ...msg,
                readBy: seenBy || msg.readBy,
              };
            }
            return msg;
          }),
        }));

        return {
          ...oldData,
          pages: updatedPages,
        };
      });

      debouncedInvalidateChats();
    });

    newSocket.on("messageDeleted", ({ messageId, chatId }) => {
      setActiveMessages((prev) => {
        const newMap = new Map(prev);
        newMap.delete(messageId);
        return newMap;
      });

      debouncedInvalidateChats();
    });

    newSocket.on(
      "messageDelivered",
      ({ messageId, chatId, deliveredAt, deliveredTo }) => {
        queryClient.setQueryData(["chatMessages", chatId], (oldData) => {
          if (!oldData) return oldData;

          const updatedPages = oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) => {
              if (msg._id === messageId) {
                return {
                  ...msg,
                  delivered: true,
                  deliveredTo: deliveredTo || msg.deliveredTo,
                  deliveredAt: deliveredAt || msg.deliveredAt,
                };
              }
              return msg;
            }),
          }));

          return {
            ...oldData,
            pages: updatedPages,
          };
        });

        debouncedInvalidateChats();
      }
    );

    return () => {
      if (invalidateChatsTimeoutRef.current) {
        clearTimeout(invalidateChatsTimeoutRef.current);
      }
      
      newSocket.removeAllListeners();
      newSocket.disconnect();
      setSocket(null);
      setOnlineUsers([]);
      setNotifications([]);
      setUnreadNotificationCount(0);
      setTotalUnreadCount(0);
      setChatUnreadCounts({});
      setConnectionStatus("disconnected");
      setActiveMessages(new Map());
    };
  }, [isAuthenticated, queryClient, getCurrentUserId, debouncedInvalidateChats, showBrowserNotification, navigate]); // ✅ Added navigate

  const setCurrentChat = useCallback((chatId) => {
    currentChatIdRef.current = chatId;
  }, []);

  const joinChat = useCallback((chatId) => {
    if (socketRef.current && chatId) {
      currentChatIdRef.current = chatId;
      socketRef.current.emit("joinChat", { chatId });
    }
  }, []);

  const leaveChat = useCallback((chatId) => {
    if (socketRef.current && chatId) {
      currentChatIdRef.current = null;
      socketRef.current.emit("leaveChat", { chatId });
    }
  }, []);

  const sendTyping = useCallback((chatId) => {
    if (socketRef.current && chatId) {
      socketRef.current.emit("typing", { chatId });
    }
  }, []);

  const stopTyping = useCallback((chatId) => {
    if (socketRef.current && chatId) {
      socketRef.current.emit("stopTyping", { chatId });
    }
  }, []);

  const markAsRead = useCallback((chatId, messageId) => {
    if (socketRef.current && chatId && messageId) {
      socketRef.current.emit("markAsRead", { chatId, messageId });
    }
  }, []);

  const clearChatNotifications = useCallback((chatId) => {
    if (socketRef.current && chatId) {
      socketRef.current.emit("clearChatNotifications", { chatId });
    }
  }, []);

  const checkUserOnline = useCallback((userId) => {
    if (socketRef.current && userId) {
      socketRef.current.emit("checkUserOnline", userId);
    }
  }, []);

  const requestUnreadCounts = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit("getUnreadCounts");
    }
  }, []);

  const getChatUnreadCount = useCallback((chatId) => chatUnreadCounts[chatId] || 0, [chatUnreadCounts]);
  
  const isUserOnline = useCallback((userId) => onlineUsers.includes(userId), [onlineUsers]);

  const clearNotification = useCallback((notificationId) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    setUnreadNotificationCount((prev) => Math.max(0, prev - 1));
  }, []);

  const markNotificationAsRead = useCallback((notificationId) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
    );
    setUnreadNotificationCount((prev) => Math.max(0, prev - 1));
  }, []);

  const markAllNotificationsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadNotificationCount(0);
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadNotificationCount(0);
  }, []);

  return (
    <SocketContext.Provider
      value={{
        socket,
        connectionStatus,
        onlineUsers,
        notifications,
        unreadNotificationCount,
        totalUnreadCount,
        chatUnreadCounts,
        activeMessages,
        setCurrentChat,
        joinChat,
        leaveChat,
        sendTyping,
        stopTyping,
        markAsRead,
        clearChatNotifications,
        checkUserOnline,
        requestUnreadCounts,
        getChatUnreadCount,
        isUserOnline,
        clearNotification,
        markNotificationAsRead,
        markAllNotificationsRead,
        clearAllNotifications,
        getCurrentUserId,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};